import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Plus, MagnifyingGlass, DownloadSimple, ArrowsLeftRight, Trash,
  Snowflake, Drop, X, Check, Clock, Funnel, CaretLeft, CaretRight,
  ListBullets, Table
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SAMPLE_TYPES = ['Cell Line', 'Plasmid', 'Bacterial Stock', 'Virus Stock', 'Protein', 'Tissue', 'RNA', 'DNA', 'Reagent', 'Other'];

// ==================== ADD SAMPLE FORM ====================
const AddSampleForm = ({ onAdded, onCancel }) => {
  const [form, setForm] = useState({
    sample_id: '', sample_type: '', owner: '', storage_type: 'freezer',
    location_id: '', box_position: '', date_stored: new Date().toISOString().split('T')[0],
    volume_ul: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [availableLocs, setAvailableLocs] = useState([]);
  const [loadingLocs, setLoadingLocs] = useState(false);

  const loadLocs = async (stype) => {
    setLoadingLocs(true);
    try {
      const locs = await api.getAvailableLocations(stype);
      setAvailableLocs(locs || []);
    } catch { setAvailableLocs([]); }
    finally { setLoadingLocs(false); }
  };

  useEffect(() => { loadLocs(form.storage_type); }, [form.storage_type]);

  const submit = async () => {
    if (!form.sample_id.trim()) { toast.error('Sample ID is required'); return; }
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.location_id) delete data.location_id;
      if (!data.box_position) delete data.box_position;
      else data.box_position = parseInt(data.box_position);
      if (data.volume_ul) data.volume_ul = parseFloat(data.volume_ul);
      else delete data.volume_ul;
      await api.addBioSample(data);
      toast.success('Sample added');
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add sample');
    } finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-6" data-testid="add-sample-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-100">Add New Sample</h3>
        <button onClick={onCancel} className="text-slate-500 hover:text-slate-300"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Sample ID *</Label>
          <Input value={form.sample_id} onChange={e => setForm({...form, sample_id: e.target.value})}
            placeholder="HEK293-GFP-P5" className="input-field w-full mt-1" data-testid="add-sample-id" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Sample Type</Label>
          <select value={form.sample_type} onChange={e => setForm({...form, sample_type: e.target.value})}
            className="input-field w-full mt-1" data-testid="add-sample-type">
            <option value="">Select...</option>
            {SAMPLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Owner</Label>
          <Input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})}
            placeholder="Name" className="input-field w-full mt-1" data-testid="add-owner" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Storage</Label>
          <select value={form.storage_type} onChange={e => setForm({...form, storage_type: e.target.value, location_id: ''})}
            className="input-field w-full mt-1" data-testid="add-storage-type">
            <option value="freezer">-80°C Freezer</option>
            <option value="ln2">LN2 Tank</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Location ID <span className="text-slate-600">(auto if blank)</span></Label>
          <select value={form.location_id} onChange={e => setForm({...form, location_id: e.target.value})}
            className="input-field w-full mt-1" data-testid="add-location-id">
            <option value="">Auto-assign</option>
            {availableLocs.map(l => (
              <option key={l.location_id} value={l.location_id}>
                {l.location_id} ({l.available} free)
              </option>
            ))}
          </select>
          {loadingLocs && <span className="text-[10px] text-slate-500">Loading...</span>}
        </div>
        <div>
          <Label className="text-xs text-slate-400">Box Position <span className="text-slate-600">(1-81, auto if blank)</span></Label>
          <Input type="number" min="1" max="81" value={form.box_position}
            onChange={e => setForm({...form, box_position: e.target.value})}
            placeholder="Auto" className="input-field w-full mt-1" data-testid="add-box-position" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Date Stored</Label>
          <Input type="date" value={form.date_stored} onChange={e => setForm({...form, date_stored: e.target.value})}
            className="input-field w-full mt-1" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Volume (µL)</Label>
          <Input type="number" value={form.volume_ul} onChange={e => setForm({...form, volume_ul: e.target.value})}
            placeholder="Optional" className="input-field w-full mt-1" data-testid="add-volume" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Notes</Label>
          <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
            placeholder="Passage #, concentration..." className="input-field w-full mt-1" data-testid="add-notes" />
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button className="btn-primary" onClick={submit} disabled={saving} data-testid="add-submit-btn">
          {saving ? '...' : <><Plus size={16} className="mr-1" /> Add Sample</>}
        </Button>
        <Button variant="outline" className="btn-secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

// ==================== MOVE MODAL ====================
const MoveModal = ({ sample, onMoved, onClose }) => {
  const [loc, setLoc] = useState('');
  const [pos, setPos] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!loc.trim()) { toast.error('Location required'); return; }
    setSaving(true);
    try {
      const data = { location_id: loc.trim(), notes };
      if (pos) data.box_position = parseInt(pos);
      await api.moveBioSample(sample.id, data);
      toast.success(`Moved to ${loc}`);
      onMoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="card-flat p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="move-modal">
        <h3 className="text-base font-semibold text-slate-100 mb-1">Move Sample</h3>
        <p className="text-sm text-slate-400 mb-4">{sample.sample_id} currently at {sample.location_id} pos {sample.box_position}</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">New Location ID *</Label>
            <Input value={loc} onChange={e => setLoc(e.target.value)}
              placeholder="FZ1-R1-R2-C3-B4 or LN2-1-C1-CA2-B3" className="input-field w-full mt-1" data-testid="move-location" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Box Position (auto if blank)</Label>
            <Input type="number" min="1" max="81" value={pos} onChange={e => setPos(e.target.value)}
              placeholder="Auto" className="input-field w-full mt-1" data-testid="move-position" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Reason for move" className="input-field w-full mt-1" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button className="btn-primary flex-1" onClick={submit} disabled={saving} data-testid="move-submit-btn">
            {saving ? '...' : <><ArrowsLeftRight size={16} className="mr-1" /> Move</>}
          </Button>
          <Button variant="outline" className="btn-secondary" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
const SampleInventoryPage = () => {
  const [overview, setOverview] = useState(null);
  const [samples, setSamples] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('inventory'); // inventory | movements
  const [showAdd, setShowAdd] = useState(false);
  const [moveSample, setMoveSample] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ storage_type: '', sample_type: '', owner: '' });

  const loadOverview = useCallback(async () => {
    try { setOverview(await api.bioInventoryOverview()); } catch {}
  }, []);

  const loadSamples = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 30 };
      if (search) params.q = search;
      if (filters.storage_type) params.storage_type = filters.storage_type;
      if (filters.sample_type) params.sample_type = filters.sample_type;
      if (filters.owner) params.owner = filters.owner;
      const data = await api.getBioSamples(params);
      setSamples(data);
      setPage(p);
    } catch { toast.error('Failed to load samples'); }
    finally { setLoading(false); }
  }, [search, filters]);

  const loadMovements = useCallback(async () => {
    try { setMovements(await api.getBioMovements({})); } catch {}
  }, []);

  useEffect(() => { loadOverview(); loadSamples(1); }, [loadOverview, loadSamples]);

  const handleDelete = async (s) => {
    if (!window.confirm(`Remove ${s.sample_id} from inventory?`)) return;
    try {
      await api.removeBioSample(s.id);
      toast.success('Sample removed');
      loadSamples(page);
      loadOverview();
    } catch { toast.error('Failed'); }
  };

  const handleExport = async (type) => {
    try {
      const token = localStorage.getItem('cl_access_token');
      const base = process.env.REACT_APP_BACKEND_URL;
      const url = type === 'movements' ? `${base}/api/bio-inventory/export/movements` : `${base}/api/bio-inventory/export`;
      const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const blob = await resp.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = type === 'movements' ? 'movement_log.csv' : 'bio_inventory.csv'; a.click();
      toast.success('Downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div data-testid="sample-inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Sample Inventory</h1>
          <p className="text-slate-500 mt-1">Unified -80°C Freezer & LN2 sample management</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary" onClick={() => handleExport(tab === 'movements' ? 'movements' : 'inventory')} data-testid="export-btn">
            <DownloadSimple size={16} className="mr-2" /> Export CSV
          </Button>
          <Button className="btn-primary" onClick={() => setShowAdd(true)} data-testid="add-sample-btn">
            <Plus size={16} className="mr-2" /> Add Sample
          </Button>
        </div>
      </div>

      {/* Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-indigo-400">{overview.total_samples}</p>
            <p className="text-xs text-slate-500">Total Samples</p>
          </div>
          <div className="card-flat p-4 text-center border border-cyan-500/15">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Snowflake size={14} className="text-cyan-400" />
              <span className="text-xs text-cyan-400 font-medium">-80°C</span>
            </div>
            <p className="text-xl font-bold text-slate-200">{overview.freezer.occupied}</p>
            <p className="text-[10px] text-slate-500">{overview.freezer.positions.toLocaleString()} total positions</p>
          </div>
          <div className="card-flat p-4 text-center border border-indigo-500/15">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Drop size={14} className="text-indigo-400" />
              <span className="text-xs text-indigo-400 font-medium">LN2</span>
            </div>
            <p className="text-xl font-bold text-slate-200">{overview.ln2.occupied}</p>
            <p className="text-[10px] text-slate-500">{overview.ln2.positions.toLocaleString()} total positions</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-slate-300">{overview.freezer.boxes}</p>
            <p className="text-xs text-slate-500">Freezer Boxes</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-slate-300">{overview.ln2.boxes}</p>
            <p className="text-xs text-slate-500">LN2 Boxes</p>
          </div>
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="mb-6">
          <AddSampleForm onAdded={() => { setShowAdd(false); loadSamples(1); loadOverview(); }} onCancel={() => setShowAdd(false)} />
        </div>
      )}

      {/* Move modal */}
      {moveSample && (
        <MoveModal sample={moveSample} onMoved={() => { setMoveSample(null); loadSamples(page); loadOverview(); }} onClose={() => setMoveSample(null)} />
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setTab('inventory')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === 'inventory' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/30'}`}
          data-testid="tab-inventory">
          <Table size={14} className="inline mr-2" /> Inventory ({samples.total})
        </button>
        <button onClick={() => { setTab('movements'); loadMovements(); }}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === 'movements' ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800/40 text-slate-400 border border-slate-700/30'}`}
          data-testid="tab-movements">
          <ListBullets size={14} className="inline mr-2" /> Movement Log
        </button>
      </div>

      {/* INVENTORY TAB */}
      {tab === 'inventory' && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-1">
              <MagnifyingGlass size={14} className="text-slate-500" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadSamples(1)}
                placeholder="Search..." className="input-field w-44 text-xs" data-testid="inv-search" />
            </div>
            <select value={filters.storage_type} onChange={e => setFilters({...filters, storage_type: e.target.value})}
              className="input-field text-xs w-32" data-testid="filter-storage">
              <option value="">All Storage</option>
              <option value="freezer">-80°C Freezer</option>
              <option value="ln2">LN2 Tank</option>
            </select>
            <select value={filters.sample_type} onChange={e => setFilters({...filters, sample_type: e.target.value})}
              className="input-field text-xs w-36" data-testid="filter-type">
              <option value="">All Types</option>
              {SAMPLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input value={filters.owner} onChange={e => setFilters({...filters, owner: e.target.value})}
              placeholder="Owner" className="input-field w-28 text-xs" data-testid="filter-owner" />
            <Button size="sm" className="btn-primary text-xs" onClick={() => loadSamples(1)} data-testid="apply-filters-btn">
              <Funnel size={12} className="mr-1" /> Filter
            </Button>
            {(search || filters.storage_type || filters.sample_type || filters.owner) && (
              <button onClick={() => { setSearch(''); setFilters({ storage_type: '', sample_type: '', owner: '' }); }}
                className="text-xs text-slate-500 hover:text-slate-300"><X size={14} /></button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-testid="inventory-table">
            <table className="freezer-table">
              <thead>
                <tr>
                  <th>Sample ID</th>
                  <th>Type</th>
                  <th>Owner</th>
                  <th>Storage</th>
                  <th>Location</th>
                  <th>Pos</th>
                  <th>Date</th>
                  <th>Vol (µL)</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {samples.items.length === 0 && !loading && (
                  <tr><td colSpan="10" className="text-center py-8 text-slate-500">No samples found. Add your first sample above.</td></tr>
                )}
                {samples.items.map(s => (
                  <tr key={s.id} data-testid={`sample-row-${s.id}`}>
                    <td className="font-medium text-slate-200">{s.sample_id}</td>
                    <td>{s.sample_type || '—'}</td>
                    <td>{s.owner || '—'}</td>
                    <td>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        s.storage_type === 'freezer' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' : 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                      }`}>
                        {s.storage_type === 'freezer' ? '-80°C' : 'LN2'}
                      </span>
                    </td>
                    <td className="font-mono text-xs text-slate-300">{s.location_id}</td>
                    <td className="text-center">{s.box_position}</td>
                    <td className="whitespace-nowrap">{s.date_stored || '—'}</td>
                    <td>{s.volume_ul ?? '—'}</td>
                    <td className="max-w-[120px] truncate">{s.notes || '—'}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setMoveSample(s)} className="text-slate-500 hover:text-indigo-400 p-1" title="Move"
                          data-testid={`move-btn-${s.id}`}>
                          <ArrowsLeftRight size={14} />
                        </button>
                        <button onClick={() => handleDelete(s)} className="text-slate-500 hover:text-red-400 p-1" title="Remove"
                          data-testid={`delete-btn-${s.id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {samples.pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button disabled={page <= 1} onClick={() => loadSamples(page - 1)}
                className="p-2 text-slate-500 hover:text-slate-300 disabled:opacity-30"><CaretLeft size={16} /></button>
              <span className="text-sm text-slate-400">Page {page} of {samples.pages}</span>
              <button disabled={page >= samples.pages} onClick={() => loadSamples(page + 1)}
                className="p-2 text-slate-500 hover:text-slate-300 disabled:opacity-30"><CaretRight size={16} /></button>
            </div>
          )}
        </>
      )}

      {/* MOVEMENTS TAB */}
      {tab === 'movements' && (
        <div className="overflow-x-auto" data-testid="movement-log">
          <table className="freezer-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Sample ID</th>
                <th>From</th>
                <th>To</th>
                <th>By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 && (
                <tr><td colSpan="7" className="text-center py-8 text-slate-500">No movements recorded yet.</td></tr>
              )}
              {movements.map(m => (
                <tr key={m.id}>
                  <td className="whitespace-nowrap">{new Date(m.timestamp).toLocaleString()}</td>
                  <td>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                      m.action === 'added' ? 'bg-emerald-500/10 text-emerald-400' :
                      m.action === 'moved' ? 'bg-amber-500/10 text-amber-400' :
                      'bg-red-500/10 text-red-400'
                    }`}>{m.action}</span>
                  </td>
                  <td className="font-medium text-slate-200">{m.sample_id}</td>
                  <td className="font-mono text-xs">{m.from_location ? `${m.from_location} #${m.from_position}` : '—'}</td>
                  <td className="font-mono text-xs">{m.to_location ? `${m.to_location} #${m.to_position}` : '—'}</td>
                  <td>{m.performed_by}</td>
                  <td className="max-w-[150px] truncate">{m.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SampleInventoryPage;
